import type { DeviceDto } from "@/lib/api/types";

import type { HmiBinding } from "../schema/hmi-bindings.schema";

export type HmiConnectionState = {
  offline: boolean;
  stale: boolean;
  latestTimestamp: string | null;
};

function formatNumeric(value: number, decimals?: number): string {
  if (!Number.isFinite(value)) {
    return String(value);
  }
  if (typeof decimals === "number") {
    return value.toFixed(decimals);
  }
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

export function formatBindingValue(
  value: string | number | boolean | null | undefined,
  binding: HmiBinding | null | undefined,
): string {
  const format = binding?.format;
  if (value === null || value === undefined || value === "") {
    return format?.nullText || "--";
  }
  const prefix = format?.prefix ?? "";
  const suffix = format?.suffix ?? "";
  const body =
    typeof value === "number"
      ? formatNumeric(value, format?.decimals ?? undefined)
      : typeof value === "boolean"
        ? value
          ? "ON"
          : "OFF"
        : String(value);
  return `${prefix}${body}${suffix}`;
}

export function formatCanvasBindingValue(
  value: string | number | boolean | null | undefined,
  binding: HmiBinding | null | undefined,
  options?: { offline?: boolean; offlineText?: string },
): string {
  if (options?.offline && binding?.source.kind === "telemetry") {
    return options.offlineText ?? "OFFLINE";
  }
  return formatBindingValue(value, binding);
}

export function getBindingLatestTimestamp(timestamps: string[] | undefined): string | null {
  if (!timestamps?.length) {
    return null;
  }
  return timestamps[timestamps.length - 1] ?? null;
}

export function getBindingConnectionState(
  binding: HmiBinding | null | undefined,
  timestamps: string[] | undefined,
  device: DeviceDto | null | undefined,
  now = Date.now(),
): HmiConnectionState {
  const latestTimestamp = getBindingLatestTimestamp(timestamps);
  const latestMs = latestTimestamp ? Date.parse(latestTimestamp) : NaN;
  const staleThresholdMs = Math.max(((timestamps?.length ?? 0) > 1 ? 0 : 1) * 60_000, 5 * 60_000);
  const stale = Number.isFinite(latestMs) ? now - latestMs > staleThresholdMs : binding?.source.kind === "telemetry";
  const offline = Boolean(device?.activity_status && device.activity_status !== "ONLINE");
  return {
    offline,
    stale,
    latestTimestamp,
  };
}
